export const ACTIVE_CALENDAR_MATCH_STATUSES = [
  "scheduled",
  "postponed",
  "in_progress",
] as const;

const activeCalendarMatchStatuses = new Set<string>(ACTIVE_CALENDAR_MATCH_STATUSES);
const bulkProgrammableCalendarStatuses = new Set<string>(["scheduled", "postponed"]);

export function isActiveCalendarStatus(status: string | null | undefined): boolean {
  return activeCalendarMatchStatuses.has(String(status ?? ""));
}

export function isBulkProgrammableCalendarStatus(status: string | null | undefined): boolean {
  return bulkProgrammableCalendarStatuses.has(String(status ?? ""));
}

// ---- Publicar horarios (Eduardo 2026-10-01) ----------------------------------
// Antes de «Publicar horarios» (por categoría y día) el jugador NO ve fecha,
// hora ni cancha de su partido y el organizador lo mueve o lo quita sin motivo
// ni aviso. El servidor manda `schedule_pending`:
// - admin: true = «sin publicar» (tiene horario y los jugadores aún no lo ven);
// - jugador: true = el servidor ya vació fecha/hora/cancha → «Horario por confirmar».
// Sin el campo (payloads viejos, cachés) se toma como PUBLICADO: es la regla
// del día del despliegue («todo lo ya programado cuenta como publicado»).

/** Lo que ve el jugador mientras su horario no se publica. */
export const SCHEDULE_PENDING_LABEL = "Horario por confirmar";

type ScheduleFlagged = {
  scheduled_date?: string | null;
  scheduled_time?: string | null;
  court?: string | null;
  status?: string | null;
  schedule_pending?: boolean | null;
};

/** ¿Tiene fecha, hora o cancha? (mismo criterio que el servidor). */
export function hasScheduleSlot(match: ScheduleFlagged): boolean {
  return Boolean(match.scheduled_date) || Boolean((match.scheduled_time ?? "").trim()) || Boolean((match.court ?? "").trim());
}

/** Vista del jugador: el servidor ocultó su horario (aún sin publicar). */
export function isSchedulePendingForPlayer(match: { schedule_pending?: boolean | null } | null | undefined): boolean {
  return match?.schedule_pending === true;
}

/** Admin: horario puesto que los jugadores aún NO ven (solo partidos por jugarse). */
export function isScheduleUnpublished(match: ScheduleFlagged): boolean {
  return match.schedule_pending === true && isActiveCalendarStatus(match.status);
}

/** Admin: el jugador NO ve este horario, sea cual sea su estado (también un
 * cancelado sin publicar: el jugador ve «Cancelado» sin hora). Para lo que se
 * reparte a los jugadores, como el calendario impreso. */
export function isScheduleHiddenFromPlayers(match: ScheduleFlagged): boolean {
  return match.schedule_pending === true;
}

/** Admin: horario puesto y ya publicado (los jugadores lo ven; moverlo avisa). */
export function isSchedulePublished(match: ScheduleFlagged): boolean {
  return hasScheduleSlot(match) && match.schedule_pending !== true;
}

/** Entra en «Publicar horarios» de ese día. */
export function isPublishableOn(match: ScheduleFlagged, date: string): boolean {
  return isScheduleUnpublished(match) && match.scheduled_date === date;
}

/** Partidos sin publicar por fecha (YYYY-MM-DD), en orden de fecha. Los que
 * tienen hora o cancha pero no fecha no se publican por día: van aparte. */
export function unpublishedByDate<T extends ScheduleFlagged>(
  matches: T[],
): { byDate: Map<string, T[]>; withoutDate: T[] } {
  const byDate = new Map<string, T[]>();
  const withoutDate: T[] = [];
  for (const match of matches) {
    if (!isScheduleUnpublished(match)) continue;
    if (!match.scheduled_date) {
      withoutDate.push(match);
      continue;
    }
    const list = byDate.get(match.scheduled_date) ?? [];
    list.push(match);
    byDate.set(match.scheduled_date, list);
  }
  const sorted = new Map([...byDate.entries()].sort(([a], [b]) => a.localeCompare(b)));
  return { byDate: sorted, withoutDate };
}
